import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { type JSX, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AlertMutators } from '~/mutators/alerts'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, SettingsContent, useSettingTitle } from '~/templates/settings'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormErrorAlert,
  FormFieldInput,
  Icon,
  processFormError,
} from '@hap/components'

export const Route = createFileRoute('/_authenticated/$alias/settings/alerts/')({
  component: AlertsSettings,
})

function AlertsSettings(): JSX.Element {
  const trpc = useTRPC()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const [newAlertDialogOpen, setNewAlertDialogOpen] = useState(false)
  const { data: alerts, isLoading } = useQuery(trpc.alerts.list.queryOptions())

  useSettingTitle([
    {
      label: 'Alerts',
      description: 'Notify the right people when service quality needs review.',
    },
  ])

  return (
    <>
      <SettingsActions>
        <Button onClick={() => setNewAlertDialogOpen(true)} icon="plus">
          New Alert
        </Button>
      </SettingsActions>
      <SettingsContent spacing="sm" className="w-full overflow-y-auto">
        {isLoading && <p className="text-accent-foreground">Loading alerts...</p>}
        {!isLoading && alerts?.length === 0 && (
          <div className="text-muted-foreground py-12 text-center">
            Create an alert to receive emails when a service-quality issue is detected.
          </div>
        )}
        <div className="grid gap-3">
          {alerts?.map((alert) => {
            const enabled = Boolean(alert.enabledAt)
            const activeSubscribers = alert.subscribers.filter(
              (subscriber) =>
                subscriber.enabledAt && !subscriber.disabledAt && !subscriber.unsubscribedAt,
            ).length

            return (
              <button
                key={alert.id}
                className="hover:bg-accent flex items-center justify-between gap-4 rounded-md border p-4 text-left"
                onClick={() =>
                  navigate({
                    to: `/$alias/settings/alerts/${alert.id}`,
                    search: location.search,
                  })
                }
              >
                <div className="min-w-0 grow">
                  <p className="truncate text-lg font-semibold">{alert.name}</p>
                  <p className="text-accent-foreground mt-1 text-sm">
                    Service quality emails
                  </p>
                </div>
                <div className="shrink-0 text-right text-sm">
                  <p className={enabled ? 'text-active' : 'text-accent-foreground'}>
                    {enabled ? 'Enabled' : 'Paused'}
                  </p>
                  <p className="text-accent-foreground mt-1">
                    {activeSubscribers} active subscriber{activeSubscribers === 1 ? '' : 's'}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </SettingsContent>
      <NewAlertDialog
        open={newAlertDialogOpen}
        onOpenChange={setNewAlertDialogOpen}
        onSuccess={(alertId) =>
          navigate({
            to: `/$alias/settings/alerts/${alertId}`,
            search: location.search,
          })
        }
      />
    </>
  )
}

const newAlertSchema = z.object({
  name: z.string().trim().min(1, 'Enter an alert name').max(120),
})

function NewAlertDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (alertId: string) => void
}): JSX.Element {
  const { create, isPending } = AlertMutators.useCreate({
    onSuccess: (alertId) => {
      onOpenChange(false)
      onSuccess(alertId)
    },
  })
  const form = useForm({
    defaultValues: { name: '' },
    resolver: zodResolver(newAlertSchema),
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await create({ name: values.name })
      form.reset()
    } catch (error) {
      processFormError(form.setError, error)
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] p-20">
        <DialogHeader>
          <DialogTitle size="lg">Create an Alert</DialogTitle>
          <DialogDescription>
            We&apos;ll email subscribers when the organization contributes to a
            service issue or an agent misses or worsens one.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="grid gap-3">
            <FormErrorAlert />
            <FormFieldInput
              control={form.control}
              name="name"
              size="lg"
              placeholder="Alert name (e.g., Escalation watch)"
            />
            <Button type="submit" size="lg" loading={isPending} loadingLabel="Creating...">
              Create Alert <Icon name="arrow-right" size={20} />
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
